import { LitElement, html, css } from 'lit'
import { navigate } from '../router.js'
import { api } from '../api.js'
import { authCardStyles } from './auth-styles.js'

// Two states in one page (§4.6):
//  - no ?token → request a reset email (forgot password)
//  - ?token=…  → set a new password
class PageReset extends LitElement {
  static properties = {
    _email: { state: true },
    _password: { state: true },
    _error: { state: true },
    _busy: { state: true },
    _done: { state: true }
  }

  static styles = authCardStyles

  constructor() {
    super()
    this._email = ''
    this._password = ''
    this._error = ''
    this._busy = false
    this._done = false
  }

  get _token() {
    return new URLSearchParams(window.location.search).get('token')
  }

  async _requestReset(e) {
    e.preventDefault()
    this._error = ''
    this._busy = true
    try {
      await api.post('/auth/password/forgot', { email: this._email.trim() })
      this._done = true
    } catch (err) {
      this._error = err.message || 'Something went wrong.'
    } finally {
      this._busy = false
    }
  }

  async _setPassword(e) {
    e.preventDefault()
    this._error = ''
    this._busy = true
    try {
      await api.post('/auth/password/reset', { token: this._token, password: this._password })
      this._done = true
    } catch (err) {
      this._error = err.code === 'RESET_INVALID'
        ? 'This reset link is invalid or has expired. Request a new one.'
        : (err.message || 'Something went wrong.')
    } finally {
      this._busy = false
    }
  }

  render() {
    const hasToken = Boolean(this._token)

    if (this._done && !hasToken) {
      return html`
        <div class="card">
          <h1>Check your email</h1>
          <div class="success">
            If an account exists for that address, we've sent a link to reset your password.
          </div>
          <div class="links" style="justify-content:center;margin-top:1.25rem">
            <a @click=${() => navigate('/login')}>Back to sign in</a>
          </div>
        </div>
      `
    }

    if (this._done && hasToken) {
      return html`
        <div class="card">
          <h1>Password updated</h1>
          <div class="success">Your password has been changed. You can now sign in.</div>
          <div class="links" style="justify-content:center;margin-top:1.25rem">
            <a @click=${() => navigate('/login')}>Go to sign in</a>
          </div>
        </div>
      `
    }

    if (hasToken) {
      return html`
        <div class="card">
          <h1>Set a new password</h1>
          <p class="sub">Choose a new password for your account.</p>
          ${this._error ? html`<div class="error">${this._error}</div>` : ''}
          <form @submit=${this._setPassword}>
            <label>New password
              <input type="password" required minlength="8" autocomplete="new-password"
                .value=${this._password} @input=${e => (this._password = e.target.value)} />
            </label>
            <button class="primary" type="submit" ?disabled=${this._busy}>
              ${this._busy ? 'Saving…' : 'Update password'}
            </button>
          </form>
        </div>
      `
    }

    return html`
      <div class="card">
        <h1>Reset your password</h1>
        <p class="sub">Enter your email and we'll send you a reset link.</p>
        ${this._error ? html`<div class="error">${this._error}</div>` : ''}
        <form @submit=${this._requestReset}>
          <label>Email
            <input type="email" required autocomplete="email"
              .value=${this._email} @input=${e => (this._email = e.target.value)} />
          </label>
          <button class="primary" type="submit" ?disabled=${this._busy}>
            ${this._busy ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
        <div class="links" style="justify-content:center">
          <a @click=${() => navigate('/login')}>Back to sign in</a>
        </div>
      </div>
    `
  }
}

customElements.define('page-reset', PageReset)
